import { useState, type FormEvent } from 'react';
import { CircleCheck } from 'lucide-react';
import { Link, useSearchParams } from 'react-router';
import { PASSWORD_MAX, PASSWORD_MIN } from '@train2volley/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AuthLayout } from '@/components/auth-layout';
import { FormError } from '@/components/form-error';
import { FormField } from '@/components/form-field';
import { authClient } from '@/lib/auth-client';
import { authErrorMessage } from '@/lib/auth-errors';

type Field = 'password' | 'repeat';

/** Nieuw wachtwoord via een herstellink van de platformbeheerder (D-034). */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get('password'));
    const repeat = String(form.get('repeat'));
    const next: Partial<Record<Field, string>> = {};
    if (password.length < PASSWORD_MIN) next.password = `Gebruik minimaal ${PASSWORD_MIN} tekens.`;
    else if (password.length > PASSWORD_MAX)
      next.password = `Gebruik maximaal ${PASSWORD_MAX} tekens.`;
    if (!next.password && password !== repeat) next.repeat = 'De wachtwoorden zijn niet gelijk.';
    setErrors(next);
    setError(null);
    if (Object.keys(next).length > 0) return;

    setPending(true);
    const res = await authClient.resetPassword({ newPassword: password, token });
    setPending(false);
    if (res.error) return setError(authErrorMessage(res.error));
    setDone(true);
  }

  if (!token) {
    return (
      <AuthLayout title="Wachtwoord instellen" description="Deze link is niet compleet.">
        <FormError message="De herstellink mist een deel. Kopieer de hele link, of vraag de beheerder om een nieuwe." />
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout
        title="Wachtwoord ingesteld"
        description="Je kunt nu inloggen met je nieuwe wachtwoord."
      >
        <Alert variant="success">
          <CircleCheck aria-hidden="true" />
          <AlertTitle>Gelukt</AlertTitle>
          <AlertDescription>
            <p>Je bent op andere apparaten uitgelogd.</p>
          </AlertDescription>
        </Alert>
        <Button asChild size="lg">
          <Link to="/inloggen">Naar inloggen</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Wachtwoord instellen"
      description="Kies een nieuw wachtwoord voor je account."
    >
      <form onSubmit={onSubmit} noValidate className="grid gap-6">
        <FormError message={error} />
        <div className="grid gap-5">
          <FormField
            id="password"
            label="Nieuw wachtwoord"
            description={`Minimaal ${PASSWORD_MIN} tekens.`}
            error={errors.password}
          >
            <Input name="password" type="password" autoComplete="new-password" />
          </FormField>
          <FormField id="repeat" label="Herhaal wachtwoord" error={errors.repeat}>
            <Input name="repeat" type="password" autoComplete="new-password" />
          </FormField>
        </div>
        <Button type="submit" size="lg" loading={pending}>
          Wachtwoord opslaan
        </Button>
      </form>
    </AuthLayout>
  );
}
